import { useCallback, useEffect, useState } from "react";
import { BrowserRouter, Link, Navigate, useLocation } from "react-router-dom";
import { TermsOfUseAcceptancePopup } from "./components/auth/TermsOfUseAcceptancePopup";
import { AdminAuthPanel } from "./components/auth/AdminAuthPanel";
import { TermsOfUseModal } from "./components/auth/TermsOfUseModal";
import { AdminBanner } from "./components/common/AdminBanner";
import { AdminLoadingView } from "./components/common/AdminLoadingView";
import { AppPopup } from "../shared/components/AppPopup";
import { AdminShell } from "./components/shell/AdminShell";
import { useAdminAuth } from "./hooks/useAdminAuth";
import { useAdminShellState } from "./hooks/useAdminShellState";
import { formatFullDateForUiLanguage } from "../shared/i18n/dateFormatting";
import { useI18n } from "../shared/i18n/i18n";
import { ensureAppFontsLoaded } from "../shared/utils/loadAppFonts";

const ADMIN_LOGIN_PATH = "/a/login";
const DEFAULT_AUTHENTICATED_PATH = "/a/organizations";
const TWO_FACTOR_RECOMMENDATION_DISMISSED_SESSION_STORAGE_KEY = "intelligo.admin.twoFactorRecommendationDismissedSession";
const MINIMUM_BOOT_LOADING_MS = 500;

const isPublicAuthPath = (pathname: string) => (
  pathname === "/a" || pathname === "/a/" || pathname === ADMIN_LOGIN_PATH
);

const resolveSafeRedirectPath = (rawRedirect: null | string) => {
  if (!rawRedirect || !rawRedirect.startsWith("/a")) {
    return DEFAULT_AUTHENTICATED_PATH;
  }
  if (rawRedirect === ADMIN_LOGIN_PATH) {
    return DEFAULT_AUTHENTICATED_PATH;
  }
  return rawRedirect;
};

type AdminAppContentProps = ReturnType<typeof useAdminAuth> & ReturnType<typeof useAdminShellState> & {
  bannerMessage: string;
  effectiveFrom: Date | null;
  hasAppFontsLoaded: boolean;
  hasMinimumBootDelayElapsed: boolean;
  isTwoFactorRecommendationDismissed: boolean;
  navigatorReloadKey: number;
  onDismissTwoFactorRecommendation: () => void;
  t: ReturnType<typeof useI18n>["t"];
};

const AdminAppContent = (props: AdminAppContentProps) => {
  const location = useLocation();
  const showAcceptancePopup =
    !!props.authUser
    && !props.latestTermsOfUseAccepted
    && !!props.effectiveFrom
    && props.effectiveFrom.getTime() <= Date.now();
  const showUpcomingTermsBanner =
    !!props.authUser
    && !props.latestTermsOfUseAccepted
    && !!props.effectiveFrom
    && props.effectiveFrom.getTime() > Date.now()
    && !props.isUpcomingTermsBannerDismissed;
  const showTwoFactorRecommendation =
    !!props.authUser
    && !!props.sessionMarker
    && !props.authUser.twoFactorMethod
    && !props.isTwoFactorRecommendationDismissed
    && location.pathname !== "/a/profile/security";
  const searchParams = new URLSearchParams(location.search);
  const redirectPath = resolveSafeRedirectPath(searchParams.get("redirect"));

  if (props.isLoading || !props.hasMinimumBootDelayElapsed || !props.hasAppFontsLoaded) {
    return <AdminLoadingView t={props.t} />;
  }

  const invitationPopup = props.panel.organizationInvitationPrompt;
  const invitationPromptPopup = (
    <AppPopup
      actions={(
        <>
          <button
            className="admin-popup-action"
            disabled={props.panel.isSubmitting}
            onClick={props.panel.actions.closeOrganizationInvitationPrompt}
            type="button"
          >
            {props.t(invitationPopup?.stage === "confirm" ? "admin.popup.cancel" : "admin.popup.close")}
          </button>
          {invitationPopup?.canConfirm ? (
            <button
              className="admin-popup-action primary"
              disabled={props.panel.isSubmitting}
              onClick={() => void props.panel.actions.confirmOrganizationInvitation()}
              type="button"
            >
              {props.t(invitationPopup.action === "accept" ? "admin.organizationInvitation.accept" : "admin.organizationInvitation.decline")}
            </button>
          ) : null}
        </>
      )}
      isOpen={Boolean(invitationPopup?.isOpen)}
      onClose={props.panel.actions.closeOrganizationInvitationPrompt}
      title={invitationPopup?.title ?? props.t("admin.organizationInvitation.title")}
    >
      <p className={invitationPopup ? `status-message is-${invitationPopup.tone}` : undefined}>
        {invitationPopup?.message}
      </p>
      {invitationPopup?.organizationName ? (
        <p className="status-message">
          <strong>{invitationPopup.organizationName}</strong>
        </p>
      ) : null}
    </AppPopup>
  );

  if (!props.authUser) {
    if (!isPublicAuthPath(location.pathname)) {
      const nextSearchParams = new URLSearchParams(location.search);
      nextSearchParams.set("redirect", `${location.pathname}${location.search}`);
      return <Navigate replace to={`${ADMIN_LOGIN_PATH}?${nextSearchParams.toString()}`} />;
    }
    if (location.pathname !== ADMIN_LOGIN_PATH) {
      return <Navigate replace to={`${ADMIN_LOGIN_PATH}${location.search}`} />;
    }

    return (
      <>
        <AdminAuthPanel
          model={props.panel}
          t={props.t}
        />
        <TermsOfUseModal
          isOpen={props.isTermsOfUseOpen}
          onClose={props.closeTermsOfUse}
          t={props.t}
        />
      </>
    );
  }

  if (location.pathname === ADMIN_LOGIN_PATH && props.isOrganizationInvitationMode) {
    return (
      <>
        <AdminLoadingView t={props.t} />
        {invitationPromptPopup}
      </>
    );
  }

  if (location.pathname === ADMIN_LOGIN_PATH) {
    return <Navigate replace to={redirectPath} />;
  }

  return (
    <>
      <AdminShell
        banner={showUpcomingTermsBanner ? (
          <AdminBanner
            color="blue"
            onClose={() => props.setIsUpcomingTermsBannerDismissed(true)}
          >
            <span>
              {props.bannerMessage}{" "}
              <button
                className="inline-link-anchor"
                onClick={props.panel.actions.openAcceptancePopup}
                type="button"
              >
                {props.t("admin.terms.bannerCta")}
              </button>
            </span>
          </AdminBanner>
        ) : null}
        currentUserName={props.authUser.name}
        currentUserSuperAdmin={props.authUser.superAdmin}
        isDesktopNavHidden={props.isDesktopNavHidden}
        isLeftPaneCollapsed={props.isLeftPaneCollapsed}
        isMobileNavOpen={props.isMobileNavOpen}
        isMobileViewport={props.isMobileViewport}
        navigatorReloadKey={props.navigatorReloadKey}
        isProfileMenuOpen={props.isProfileMenuOpen}
        leftPaneWidth={props.leftPaneWidth}
        onCloseDesktopNav={() => props.setIsDesktopNavHidden(true)}
        onCloseMobileNav={() => props.setIsMobileNavOpen(false)}
        onOpenDesktopNav={() => props.setIsDesktopNavHidden(false)}
        onSignOut={() => {
          props.setIsMobileNavOpen(false);
          props.setIsProfileMenuOpen(false);
          void props.signOut();
        }}
        onStartLeftPaneResize={props.startLeftPaneResize}
        onToggleLeftPane={() => props.setIsLeftPaneCollapsed((current) => !current)}
        onToggleMobileNav={() => props.setIsMobileNavOpen((current) => !current)}
        onToggleProfileMenu={() => props.setIsProfileMenuOpen((current) => !current)}
        profileMenuRef={props.profileMenuRef}
        t={props.t}
      />
      <TermsOfUseAcceptancePopup
        dismissible={!showAcceptancePopup}
        isOpen={showAcceptancePopup || props.isAcceptancePopupOpen}
        isSubmitting={props.panel.isSubmitting}
        onAccept={() => void props.panel.actions.acceptLatestTermsOfUse()}
        onClose={props.closeAcceptancePopup}
        onOpenTermsOfUse={props.panel.actions.openTermsOfUse}
        t={props.t}
      />
      <TermsOfUseModal
        isOpen={props.isTermsOfUseOpen}
        onClose={props.closeTermsOfUse}
        t={props.t}
      />
      <AppPopup
        actions={(
          <>
            <button className="admin-popup-action" onClick={props.onDismissTwoFactorRecommendation} type="button">
              {props.t("admin.popup.cancel")}
            </button>
            <Link
              className="admin-popup-action primary"
              onClick={props.onDismissTwoFactorRecommendation}
              to="/a/profile/security"
            >
              {props.t("admin.twoFactor.recommendationCta")}
            </Link>
          </>
        )}
        isOpen={showTwoFactorRecommendation}
        onClose={props.onDismissTwoFactorRecommendation}
        title={props.t("admin.twoFactor.recommendationTitle")}
      >
        <p>{props.t("admin.twoFactor.recommendationBody.account")}</p>
      </AppPopup>
      {invitationPromptPopup}
    </>
  );
};

export const AdminApp = () => {
  const { language, t } = useI18n();
  const [navigatorReloadKey, setNavigatorReloadKey] = useState(0);
  const [hasAppFontsLoaded, setHasAppFontsLoaded] = useState(false);
  const [hasMinimumBootDelayElapsed, setHasMinimumBootDelayElapsed] = useState(false);
  const handleOrganizationInvitationAccepted = useCallback(() => {
    setNavigatorReloadKey((current) => current + 1);
  }, []);
  const auth = useAdminAuth(t, handleOrganizationInvitationAccepted);
  const shell = useAdminShellState();
  const [dismissedTwoFactorRecommendationSession, setDismissedTwoFactorRecommendationSession] = useState(
    window.localStorage.getItem(TWO_FACTOR_RECOMMENDATION_DISMISSED_SESSION_STORAGE_KEY)
  );
  const latestTermsSummary = auth.latestTermsOfUseSummary;
  const effectiveFrom = latestTermsSummary ? new Date(latestTermsSummary.effectiveFrom) : null;
  const formattedEffectiveFrom = effectiveFrom
    ? formatFullDateForUiLanguage(latestTermsSummary!.effectiveFrom, language)
    : "";
  const bannerMessage = t("admin.terms.bannerMessage").replace("{effectiveDate}", formattedEffectiveFrom);
  const isTwoFactorRecommendationDismissed =
    !!auth.sessionMarker && dismissedTwoFactorRecommendationSession === auth.sessionMarker;

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setHasMinimumBootDelayElapsed(true);
    }, MINIMUM_BOOT_LOADING_MS);
    return () => window.clearTimeout(timeoutId);
  }, []);

  useEffect(() => {
    let cancelled = false;
    void ensureAppFontsLoaded().then(() => {
      if (!cancelled) {
        setHasAppFontsLoaded(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const dismissTwoFactorRecommendation = () => {
    if (auth.sessionMarker) {
      setDismissedTwoFactorRecommendationSession(auth.sessionMarker);
      window.localStorage.setItem(TWO_FACTOR_RECOMMENDATION_DISMISSED_SESSION_STORAGE_KEY, auth.sessionMarker);
    }
  };

  return (
    <BrowserRouter>
      <AdminAppContent
        {...auth}
        {...shell}
        bannerMessage={bannerMessage}
        effectiveFrom={effectiveFrom}
        hasAppFontsLoaded={hasAppFontsLoaded}
        hasMinimumBootDelayElapsed={hasMinimumBootDelayElapsed}
        isTwoFactorRecommendationDismissed={isTwoFactorRecommendationDismissed}
        navigatorReloadKey={navigatorReloadKey}
        onDismissTwoFactorRecommendation={dismissTwoFactorRecommendation}
        t={t}
      />
    </BrowserRouter>
  );
};
